import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import ErrorBox from '../../core/components/ErrorBox';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import useYearlyStatistics from '../hooks/useYearlyStatistics';
import BookingStats from './BookingStats';

const StatsStatistics = () => {
    const propertyId = usePropertyId();
    const navigate = useNavigate();
    const { data, loading, error } = useYearlyStatistics(propertyId);

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" gap={3} p={5}>
                <Skeleton variant="text" height={50} />
                <Skeleton variant="rectangular" height={400} />
            </Box>
        );
    }

    if (error) return <ErrorBox error={error} />;

    if (!data || !data.hasPreset) {
        return (
            <Box
                sx={{
                    p: 5,
                    textAlign: 'center',
                    border: '3px solid #374151',
                    borderRadius: 0,
                    backgroundColor: '#1a1a2e',
                    m: 3,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 2,
                    }}
                >
                    Tax Profile Required
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: 'text.secondary', mb: 3 }}
                >
                    Configure a tax profile for this property to see detailed tax breakdowns
                    in your statistics.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate({ to: `/${propertyId}/settings/general` })}
                    sx={{
                        borderRadius: 0,
                        border: '3px solid #6366f1',
                        backgroundColor: '#6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                            backgroundColor: '#4f46e5',
                            borderColor: '#4f46e5',
                        },
                    }}
                >
                    Configure Tax Profile
                </Button>
            </Box>
        );
    }

    if (data.years.length === 0) {
        return (
            <Box
                sx={{
                    p: 5,
                    textAlign: 'center',
                    border: '3px solid #374151',
                    borderRadius: 0,
                    backgroundColor: '#1a1a2e',
                    m: 3,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 2,
                    }}
                >
                    No Bookings Found
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Add some bookings to see your yearly statistics.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                gap: 3,
                p: 3,
            }}
        >
            {data.years.map((yearData) => (
                <BookingStats key={yearData.year} taxBreakdown={yearData} />
            ))}
        </Box>
    );
};

export default StatsStatistics;
